import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { translations, type Lang, type Dict } from "./translations";

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Dict;
}

const Ctx = createContext<I18nCtx | null>(null);

function detectFromNavigator(): Lang {
  if (typeof navigator === "undefined") return "en";
  const nav = navigator.languages?.[0] || navigator.language || "en";
  return nav.toLowerCase().startsWith("de") ? "de" : "en";
}

function parseLang(raw: unknown): Lang | null {
  return raw === "de" || raw === "en" ? raw : null;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  // Read `?lang=` from the URL. Works both on the server (SSR) and on the
  // client, so Google crawlers hitting `/wissenschaft?lang=de` get German
  // HTML on first byte.
  const search = useRouterState({ select: (s) => s.location.search as { lang?: string } });
  const urlLang = parseLang(search?.lang);

  const [lang, setLangState] = useState<Lang>(urlLang ?? "en");

  // Client-side hydration: if the URL didn't pin a language, fall back to
  // saved preference or the browser locale.
  useEffect(() => {
    if (urlLang) {
      setLangState(urlLang);
      document.documentElement.lang = urlLang;
      return;
    }
    const saved = parseLang(localStorage.getItem("lang"));
    const initial = saved ?? detectFromNavigator();
    setLangState(initial);
    document.documentElement.lang = initial;
  }, [urlLang]);

  const setLang = (l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem("lang", l);
    } catch {
      /* ignore storage errors */
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = l;
    }
    // Reflect the choice in the URL so refresh + share links stay stable
    // and Google can index the correct variant.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("lang", l);
      window.history.replaceState({}, "", url.toString());
    }
  };

  return (
    <Ctx.Provider value={{ lang, setLang, t: translations[lang] }}>{children}</Ctx.Provider>
  );
}

export function useI18n() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useI18n outside provider");
  return c;
}
